import Dialog from '../Dialog';
import { Box, DialogContent, Typography, Stack, Button, Checkbox } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useResponsive from '../../hooks/useResponsive';
import DialogTitle from '../DialogTitle';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { PATH_MAIN } from 'src/routes/paths';
import useCCReferral from 'src/hooks/useCCReferral';
import useLocales from 'src/hooks/useLocales';

interface Props {
    open: boolean;
    onClose: () => void;
    onOpenTerms: () => void;
}

export default function JoinCareCircleDialog({ open, onClose, onOpenTerms }: Props) {
    const isDesktop = useResponsive('up', 'lg');
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { translate } = useLocales();
    const [agreed, setAgreed] = useState(false)

    const { generateReferralCode, generateLoading } = useCCReferral()

    const handleJoinCareCircle = useCallback(async () => {
        const result = await generateReferralCode({
            onError: (error) => {
                enqueueSnackbar(error.message, { variant: "error" })
            },
        })
        if (result.data?.generateReferralCode?.ccReferralCode) {
            onClose()
            navigate(PATH_MAIN.general.careCircle)
        }
    }, [generateReferralCode, navigate, onClose, enqueueSnackbar])

    const DATA = [
        {
            title: translate('components.careCircle.join.details.earnTitle'),
            desc: translate('components.careCircle.join.details.earnDesc'),
            icon: "/assets/icons/carecircle/diamond.svg"
        },
        {
            title: translate('components.careCircle.join.details.keepTitle'),
            desc: translate('components.careCircle.join.details.keepDesc'),
            icon: "/assets/icons/carecircle/gift.svg"
        },
        {
            title: translate('components.careCircle.join.details.shareTitle'),
            desc: translate('components.careCircle.join.details.shareDesc'),
            icon: "/assets/icons/carecircle/share.svg"
        },
        {
            title: translate('components.careCircle.join.details.impactTitle'),
            desc: translate('components.careCircle.join.details.impactDesc'),
            icon: "/assets/icons/carecircle/heart-hand.svg"
        },
    ]

    return (
        <Dialog
            onClose={onClose}
            aria-labelledby={`registration-dialog`}
            open={open}
            maxWidth={isDesktop ? 'sm' : 'xs'}
            fullWidth
        >
            <DialogTitle onClose={onClose}>
                <></>
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", rowGap: 3 }}>
                <Box
                    component="img"
                    src="/assets/icons/carecircle/cc-banner.svg"
                    alt=""
                    sx={{ width: "100%" }}
                    />
                <Stack>
                    <Typography sx={{ textAlign: "center" }} variant="h6">{translate('components.careCircle.join.title')}</Typography>
                    <Typography sx={{ textAlign: "center" }} variant="body1">{translate('components.careCircle.join.subtitle')}</Typography>
                    <Typography sx={{ textAlign: "center" }} color={"text.secondary"} variant="body1">{translate('components.careCircle.join.intro1')}</Typography>
                    <Typography sx={{ textAlign: "center" }} color={"text.secondary"} variant="body1">{translate('components.careCircle.join.intro2')}</Typography>
                </Stack>
                <Stack spacing={2}>
                    <Typography style={{ textAlign: "center" }} variant="subtitle2">{translate('components.careCircle.join.whatsInIt')}</Typography>
                    {
                        DATA.map(d => (
                            <Box key={d.title} sx={{
                                display: "flex",
                                flexDirection: "row",
                                columnGap: 2,
                                p: 2,
                                borderRadius: 2,
                                alignItems: "center",
                                background: (theme) => theme.palette.mode === 'dark'
                                    ? "linear-gradient(to right, #0D4444, #131842)"
                                    : "linear-gradient(to right, #EAFFFD, #D8E2FF)"
                            }}>
                                <Box
                                component="img"
                                src={d.icon}
                                alt=""
                                sx={{ width: 32, height: 32 }}
                                />
                                <Stack>
                                    <Typography variant="subtitle2">{d.title}</Typography>
                                    <Typography variant="body2">{d.desc}</Typography>
                                </Stack>
                            </Box>
                        ))
                    }
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ alignSelf: "center" }}>
                    <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                    <Typography variant="body2">
                        {translate('components.careCircle.join.agreeTo')}{" "}
                        <Button
                        onClick={onOpenTerms}
                        variant="text"
                        sx={{
                            p: 0,
                            minWidth: 0,
                            textTransform: "none",
                            verticalAlign: "baseline",
                        }}
                        >
                        {translate('components.careCircle.join.termsAndConditions')}
                    </Button>
                    </Typography>
                </Stack>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    sx={{ alignSelf: "center", mb: 2 }}
                    disabled={!agreed || generateLoading}
                    loading={generateLoading}
                    onClick={handleJoinCareCircle}
                >
                    {translate('components.careCircle.join.cta')}
                </LoadingButton>
            </DialogContent>
        </Dialog>
    );
}


